/**
 * useFunnelInteractions Hook - MA-ARCH-164
 * 
 * Manages funnel interaction handlers and derived interaction state.
 * Extracted from FunnelVisualization.jsx.
 */

import { useCallback, useMemo } from 'react';
import {
  NODE_HEIGHT,
  NODE_HEIGHT_HOVERED_MIN,
  HOVER_UPWARD_SHIFT,
  CONNECTOR_BORDER_OFFSET,
  CONNECTOR_SELECTION_RING_OFFSET,
  CONNECTOR_HOVER_BORDER_OFFSET,
  CONNECTOR_NON_HIGHLIGHTED_OFFSET,
} from '../utils/layoutConstants';

/**
 * Hook for funnel interaction handlers and derived state.
 * 
 * @param {Object} params - Parameters
 * @param {string|null} params.selectedNodeId - Currently selected node
 * @param {string|null} params.hoveredNodeId - Currently hovered node
 * @param {string|null} params.exitingNodeId - Node in exit transition
 * @param {string|null} params.geometryNodeId - Node for geometry calculations
 * @param {Map} params.nodeHeights - Measured node heights
 * @param {Object} params.nodeHeightsRef - Ref to node heights
 * @param {number|null} params.expandedHeight - Preserved expanded height
 * @param {Function} params.onNodeClick - Click callback
 * @returns {Object} - Interaction handlers and helpers
 */
export function useFunnelInteractions({
  selectedNodeId,
  hoveredNodeId,
  exitingNodeId,
  geometryNodeId,
  nodeHeights,
  nodeHeightsRef,
  expandedHeight,
  onNodeClick,
}) {
  /**
   * Handle node click.
   */
  const handleNodeClick = useCallback((nodeId) => {
    onNodeClick?.(nodeId);
  }, [onNodeClick]);
  
  /**
   * Check if an edge should be highlighted.
   * @param {Object} edge - The edge object
   * @returns {boolean}
   */
  const isEdgeHighlighted = useCallback((edge) => {
    const activeNodeId = hoveredNodeId || selectedNodeId;
    if (!activeNodeId) return false;
    return edge.source_node_id === activeNodeId;
  }, [hoveredNodeId, selectedNodeId]);
  
  /**
   * Get actual node height for connector anchoring.
   * Accounts for hover state, exit transition, and selection ring.
   * 
   * @param {string} nodeId - The node ID
   * @param {boolean} isSourceInGeometryState - Is source in geometry state
   * @param {boolean} isSourceSelected - Is source selected
   * @returns {number} - The calculated height
   */
  const getActualNodeHeight = useCallback((nodeId, isSourceInGeometryState, isSourceSelected) => {
    let height;
    
    // During exit animation, use preserved expanded height for the exiting node
    if (exitingNodeId && nodeId === exitingNodeId && expandedHeight) {
      height = expandedHeight - HOVER_UPWARD_SHIFT;
    } else {
      // Normal path: use measured height
      const measuredHeight = nodeHeightsRef?.current?.get(nodeId) || nodeHeights?.get(nodeId);
      
      if (measuredHeight) {
        // For nodes in geometry state (hovered or exiting), account for upward shift
        height = isSourceInGeometryState ? measuredHeight - HOVER_UPWARD_SHIFT : measuredHeight;
      } else {
        height = NODE_HEIGHT;
      }
    }
    
    // Add border offset for visual edge alignment
    height += CONNECTOR_BORDER_OFFSET;
    
    // Add hover border offset if the source node is in geometry state
    if (isSourceInGeometryState) {
      height += CONNECTOR_HOVER_BORDER_OFFSET;
    }
    
    // Add selection ring offset if the source node is selected
    if (isSourceSelected) {
      height += CONNECTOR_SELECTION_RING_OFFSET;
    }
    
    return height;
  }, [nodeHeights, nodeHeightsRef, exitingNodeId, expandedHeight]);
  
  /**
   * Get adjusted position for a node (base + clearance offset).
   * @param {string} nodeId - The node ID
   * @param {Object} basePos - The base position
   * @param {Map} clearanceOffsets - Clearance offset map
   * @returns {Object} - Adjusted position
   */
  const getAdjustedPosition = useCallback((nodeId, basePos, clearanceOffsets) => {
    const offset = clearanceOffsets?.get(nodeId) || 0;
    return {
      ...basePos,
      y: basePos.y + offset,
    };
  }, []);
  
  return {
    handleNodeClick,
    isEdgeHighlighted,
    getActualNodeHeight,
    getAdjustedPosition,
  };
}

export default useFunnelInteractions;
